import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import { getUser, getUserInformation } from '../utils/Common';
import { 
  getBuilder, 
  getCapsByBuilder, 
  getUsersByBuilder, 
  getUserCapsByBuilderForOwners, 
  getLatestUserCapAction, 
  patchUser, 
  delSharing, 
  patchSharing, 
  shareCapWithUser, 
  getUsersBySharedOnCapsByBuilder,
  getSharedCaps,
  getCapsByUserId,
  recordUserAction,
  postShareWithEmail
} from '../utils/bedrockAPI';
import { ThreeDots } from "react-loader-spinner";
import { useHistory } from 'react-router-dom';
import "./AccessControl.css";

// Import components
import AccessControlHeader from '../components/user-access/AccessControlHeader';
import UserInfoPanel from '../components/user-access/UserInfoPanel';
import AccessSummaryPanel from '../components/user-access/AccessSummaryPanel';
import CameraAccessTable from '../components/user-access/CameraAccessTable';
import SharedCamerasTable from '../components/user-access/SharedCamerasTable';
import AccessControlModal from '../components/user-access/AccessControlModal';
import GeneralSettingsPanel from '../components/user-access/GeneralSettingsPanel';
import AccessLevelDescriptions from '../components/access-control/AccessLevelDescriptions';
import LoadingSpinner from '../components/access-control/LoadingSpinner';

export default function UserAccessDetail(props) {
  const history = useHistory();
  const [builder, setBuilder] = useState(null);
  const [caps, setCaps] = useState([]);
  const [user, setUser] = useState(null);
  const [userCaps, setUserCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(getUser());
  const [copied, setCopied] = useState(false);
  const [lastAccessTimes, setLastAccessTimes] = useState({});
  const [loadingAccessTimes, setLoadingAccessTimes] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAccessChange, setPendingAccessChange] = useState(null);
  const [otherCaps, setOtherCaps] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAccessInfo, setShowAccessInfo] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadBuilder(builderId) {
      const tempBuilder = await getBuilder(builderId);
      if(isMounted) {
        setBuilder(tempBuilder);
      }
    }
    if(props.match.params.builderid) {
      loadBuilder(props.match.params.builderid);
    }
    recordUserAction(getUser().userId, "user access detail", null, {'userData': getUserInformation(),userId:props.match.params.userid});
    return () => {
      isMounted = false;
    };
  },[props.match.params.builderid]);

  useEffect(() => {
    async function loadData(builderId) {
      const tempCaps = await getCapsByBuilder(builderId);
      tempCaps.sort((a,b) => a.name < b.name ? -1 : 1);
      setCaps(tempCaps);

      let tempUsers = await getUsersByBuilder(builderId);
      let targetUser = tempUsers.find(u => u.userId === props.match.params.userid);
      if(!targetUser) {
        tempUsers = await getUsersBySharedOnCapsByBuilder(builderId);
        targetUser = tempUsers.find(u => u.userId === props.match.params.userid);
      }
      setUser(targetUser);
      setCurrentUser(getUser());

      const tempUserCaps = await getUserCapsByBuilderForOwners(builderId);
      setUserCaps(tempUserCaps);
      setLoading(false);
    }
    if(builder) {
      loadData(builder.builderId);
    }
  },[builder]);

  useEffect(() => {
    async function loadLastAccessTimes() {
      if (user && caps.length > 0 && loadingAccessTimes) {
        const accessTimes = {};
        for (const cap of caps) {
          try {
            const lastAction = await getLatestUserCapAction(user.userId, cap.capId);
            if (lastAction && lastAction.actionDate) {
              accessTimes[cap.capId] = new Date(lastAction.actionDate);
            }
          } catch (error) {
            console.error(`Error fetching last access time for cap ${cap.capId}:`, error);
          }
        }
        setLastAccessTimes(accessTimes);
        setLoadingAccessTimes(false);
      }
    }
    async function loadOtherCaps() {
      let tempOtherUserCaps = await getSharedCaps(user.userId);
      let tempOtherCaps = await getCapsByUserId(user.userId);
      tempOtherCaps = tempOtherCaps.filter(cap => cap.builderId !== builder.builderId);
      for(var i=0; i<tempOtherCaps.length; i++) {
        if(tempOtherCaps[i].builderId !== builder.builderId && tempOtherCaps[i].builderId) {
          tempOtherCaps[i].builder = await getBuilder(tempOtherCaps[i].builderId);
        } else {
          tempOtherCaps[i].builder = builder;
        }
      }
      
      // Join the arrays by capId
      const joinedCaps = tempOtherUserCaps
        .filter(userCap => tempOtherCaps.some(cap => cap.capId === userCap.capId))
        .map(userCap => {
          const cap = tempOtherCaps.find(c => c.capId === userCap.capId);
          return {
            ...userCap,
            ...cap
          };
        });
      
      setOtherCaps(joinedCaps);
    }
    loadLastAccessTimes();
    if(user && getUser().role==='ADMIN') {
      loadOtherCaps();
    }
  }, [user, caps]);

  const handleAccessChange = async (userId, capId, newType) => {
    let userCap = userCaps.find(uc => uc.userId === userId && uc.capId === capId);
    if(!userCap) {
      userCap = otherCaps.find(uc => uc.userId === userId && uc.capId === capId);
    }
    if(newType === 'No Access') {
      if(!userCap) {
        return;
      }
      setUserCaps(userCaps.filter(uc => uc.userId !== userId || uc.capId !== capId));
      await delSharing(userCap.userCapId);
    } else if(userCap) {
      if(userCap.accessType === newType) {
        return;
      }
      setUserCaps(userCaps.map(uc => uc.userCapId === userCap.userCapId ? {...uc, accessType: newType} : uc));
      userCap.accessType = newType;
      await patchSharing(userCap.userCapId, userCap);
    } else {
      let newUserCap = await shareCapWithUser(capId, userId, newType);
      setUserCaps([...userCaps, newUserCap.data]);
    }
  };

  const handleAccessChangeRequest = (userId, newType) => {
    setPendingAccessChange({ userId, newType });
    setShowConfirmModal(true);
  };

  const handleConfirmAccessChange = async () => {
    if (pendingAccessChange) {
      await changeAllAccess(pendingAccessChange.userId, pendingAccessChange.newType);
      setShowConfirmModal(false);
      setPendingAccessChange(null);
    }
  };

  const handleCancelAccessChange = () => {
    setShowConfirmModal(false);
    setPendingAccessChange(null);
  };

  const shareWithEmail = async (email,userId) => {
    let shareableUserCap = null;
    for(const userCap of userCaps) {
      if(userCap.userId===userId) {
        shareableUserCap = userCap;
        break;
      }
    }
    if(!shareableUserCap) {
      return;
    }
    await postShareWithEmail(shareableUserCap.capId,email,shareableUserCap.accessType,shareableUserCap.power,true);
  }

  const changeAllAccess = async (userId, newType) => {
    let updatedUserCaps = [...userCaps];
    
    for(const cap of caps) {
      let userCap = updatedUserCaps.find(uc => uc.userId === userId && uc.capId === cap.capId);
      
      if(newType === 'No Access') {
        if(!userCap) continue;
        updatedUserCaps = updatedUserCaps.filter(uc => uc.userId !== userId || uc.capId !== cap.capId);
        await delSharing(userCap.userCapId);
      } else if(userCap) {
        if(userCap.accessType === newType) continue;
        userCap.accessType = newType;
        await patchSharing(userCap.userCapId, userCap);
      } else {
        let newUserCap = await shareCapWithUser(cap.capId, userId, newType);
        updatedUserCaps.push(newUserCap.data);
      }
    }
    
    setUserCaps(updatedUserCaps);
  }

  const toggleBuilderAdmin = async () => {
    try {
      const newStatus = user.builderStatus === 'ADMIN' ? null : 'ADMIN';
      await patchUser(user.userId, { builderStatus: newStatus });
      setUser(prev => ({ ...prev, builderStatus: newStatus }));
    } catch (error) {
      console.error('Error updating builder admin status:', error);
    }
  };

  const toggleBillingAccess = async () => {
    try {
      const newStatus = user.builderStatus === 'BILLING' ? null : 'BILLING';
      await patchUser(user.userId, { builderStatus: newStatus });
      setUser(prev => ({ ...prev, builderStatus: newStatus }));
    } catch (error) {
      console.error('Error updating billing access status:', error);
    }
  };

  const onCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatLastAccessTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return (
      <div>
        <div>{date.toLocaleDateString()}</div>
        <div className="user-camera-access-time-sub">{date.toLocaleTimeString()}</div>
      </div>
    );
  };

  const renderLastAccess = (capId) => {
    if (loadingAccessTimes) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <ThreeDots color="#00BFFF" height={20} width={20} />
        </div>
      );
    }
    return formatLastAccessTime(lastAccessTimes[capId]);
  };

  const handleKeyPress = (e, field) => {
    if (e.key === 'Enter') {
      handleEditSave(field);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const handleNotificationChange = async (userId, capId, notificationType, value) => {
    const userCap = userCaps.find(uc => uc.userId === userId && uc.capId === capId);
    if (!userCap) return;

    try {
      await patchSharing(userCap.userCapId, { [notificationType]: value });
      
      // Update local state
      setUserCaps(prevCaps => 
        prevCaps.map(uc => 
          uc.userCapId === userCap.userCapId 
            ? { ...uc, [notificationType]: value }
            : uc
        )
      );

      // Update otherCaps if this is a shared camera
      if (otherCaps.some(oc => oc.capId === capId)) {
        setOtherCaps(prevCaps =>
          prevCaps.map(oc =>
            oc.capId === capId
              ? { ...oc, [notificationType]: value }
              : oc
          )
        );
      }
    } catch (error) {
      console.error('Error updating notification settings:', error);
      // You might want to show an error message to the user here
    }
  };

  if (loading) {
    return (
      <div className="access-control-page user-access-detail-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="access-control-page user-access-detail-page">
        <p className="access-control-empty-state">User not found</p>
      </div>
    );
  }

  if (currentUser && currentUser.builderStatus !== 'ADMIN' && currentUser.role !== 'ADMIN') {
    return (
      <div className="access-control-page user-access-detail-page">
        <p className="access-control-empty-state">You are not authorized to view this page</p>
      </div>
    );
  }

  return (
    <div className="access-control-page user-access-detail-page">
      <AccessControlHeader
        user={user}
        builder={builder}
        history={history}
        copied={copied}
        onCopy={onCopy}
        isMobile={isMobile}
      />
      {user.role === 'SHARE_CREATE' && (
        <Alert bsStyle="warning" className="user-access-activation-alert">
          <i className="fas fa-exclamation-circle" /> This user has not yet activated their account.
        </Alert>
      )}

      <Row className="user-access-detail-grid">
        <Col md={4} className="user-access-detail-sidebar">
          <UserInfoPanel
            user={user}
            setUser={setUser}
            isMobile={isMobile}
            shareWithEmail={shareWithEmail}
            builder={builder}
            history={history}
            userCaps={userCaps}
          />

          <AccessSummaryPanel
            user={user}
            caps={caps}
            userCaps={userCaps}
            isMobile={isMobile}
          />
        </Col>

        <Col md={8} className="user-access-detail-main">
          <GeneralSettingsPanel
            user={user}
            toggleBuilderAdmin={toggleBuilderAdmin}
            toggleBillingAccess={toggleBillingAccess}
            handleAccessChangeRequest={changeAllAccess}
            isMobile={isMobile}
            builder={builder}
          />

          <section className="access-control-panel">
            <div className="access-control-section-header">
              <h2 className="access-control-section-title">Camera Access Control</h2>
              <div className="header-buttons">
                <Button
                  className="btn-brand-outline help-button"
                  onClick={() => setShowAccessInfo(!showAccessInfo)}
                >
                  <i className="fas fa-question-circle" /> Help
                </Button>
              </div>
            </div>
            {showAccessInfo && (
              <AccessLevelDescriptions isMobile={isMobile} showExtraInfo={false} />
            )}
            <div className="access-control-table-scroll">
              <CameraAccessTable
                caps={caps}
                userCaps={userCaps}
                user={user}
                handleAccessChange={handleAccessChange}
                handleNotificationChange={handleNotificationChange}
                renderLastAccess={renderLastAccess}
                isMobile={isMobile}
                history={history}
              />
            </div>
            {getUser().role === 'ADMIN' && otherCaps.length > 0 && (
              <div className="user-access-shared-section">
                <h3 className="access-control-section-title access-control-section-title--spaced">
                  Cameras Shared from Other Builders
                  <span className="access-control-meta"> (Bedrock only)</span>
                </h3>
                <div className="access-control-table-scroll">
                  <SharedCamerasTable
                    otherCaps={otherCaps}
                    user={user}
                    handleAccessChange={handleAccessChange}
                    renderLastAccess={renderLastAccess}
                    isMobile={isMobile}
                    history={history}
                  />
                </div>
              </div>
            )}
          </section>
        </Col>
      </Row>

      <AccessControlModal
        showConfirmModal={showConfirmModal}
        handleCancelAccessChange={handleCancelAccessChange}
        handleConfirmAccessChange={handleConfirmAccessChange}
        pendingAccessChange={pendingAccessChange}
      />
    </div>
  );
} 